import React, { useEffect, useState, useContext } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import PostSaleSection from './PostSaleSection/PostSaleSection';
import { LinkWithNativeEvents } from '@components/_common/link/link';
import { dmvWarningText, SECTION_TYPE, PROCESSED_STATUS } from './constants';
import { RegistrationAnalytics } from '../../constants';
import { Label, MailedOn, MailingInfo, Tracking, Value, WarningBox, DocUploadArea } from './RegistrationCard.styles';
import { MultilineText } from './PostSaleSection/styles/PostSaleSection.styles';
import { SubTitle } from './TemporaryOperationPlateSubtitle';
import DocumentUpload from './DocumentUpload/DocumentUpload';
import Dropdown from './Dropdown/Dropdown';
import TrackerAnchor from './Tracker/TrackerAnchor';
import { getContentText } from './getContextText';
import MissingDocument from './MissingDocument/MissingDocument';
import { PaperWarning } from './MissingDocument/MissingDocument.styles';
import { Dropzone } from './DocumentUpload/styles/DocumentUpload.styles';

const POSSIBLE_DOCS = [
  ' - SELECT - ',
  'Bill of Sale',
  'Compliance Document',
  'Emissions',
  'Insurance for Carvana Vehicle',
  'Military Document',
  'Paid Property Tax',
  'Address Verification',
  'Retail Purchase Agreement',
  'Security Agreement',
  'Tax Form',
  'Title Application',
  "Valid Driver's License",
  'Vehicle Inspection',
  'VIN/Safety inspection',
];

type RegistrationCardProps = {
  registrationStatus: {
    estimate: {
      estimateEndDate: string;
      estimateStartDate: string;
      overlapsTop: boolean;
      registrationStatus: string;
      submissionMethod: string;
      submittedAt: string;
      topExpirationDate: string;
    };
    status: string;
    showDocumentUpload: boolean;
    missingDocuments: string;
    missingElectronicDocuments: string[];
    missingPaperDocuments: string[];
    state: keyof typeof dmvWarningText | string;
    hasTrackingInformation: boolean;
    trackingInformation: any;
    hasTransferTag: boolean;
    methodOfSubmission: string;
    statusCode: string;
    isSelfRegistered: boolean;
    tempPlateExpirationDate: Date;
    isDealerInState: boolean;
    extendedTOPDocument?: string;
    daysLeft?: number;
    completedOn?: string;
    regError: boolean;
  };
  vehicleId: string;
};

export const RegistrationCard = ({ registrationStatus, vehicleId }: RegistrationCardProps) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const [selectedDocument, setSelectedDocument] = useState(POSSIBLE_DOCS[0]);
  const [possibleDocuments, setPossibleDocuments] = useState(POSSIBLE_DOCS);

  const {
    missingDocuments,
    missingElectronicDocuments,
    missingPaperDocuments,
    showDocumentUpload,
    state,
    isDealerInState,
    hasTrackingInformation,
    trackingInformation,
    estimate,
    extendedTOPDocument,
    status,
    statusCode,
    isSelfRegistered,
    daysLeft,
    completedOn,
    regError,
  } = registrationStatus;

  const hasMissingDocuments =
    missingElectronicDocuments.length > 0 ||
    (missingPaperDocuments.length > 0 && (missingElectronicDocuments[0] !== '' || missingPaperDocuments[0] !== ''));

  const hasRegError = regError || false;

  const isRegLoading = !registrationStatus;

  const tempPlateExpirationDate = estimate && estimate.topExpirationDate;

  const shouldShowMailingInformation: boolean =
    hasTrackingInformation && trackingInformation && status === PROCESSED_STATUS.COMPLETED;

  useEffect(() => {
    if ((state as any) === 'FL') {
      setPossibleDocuments([...POSSIBLE_DOCS, 'Hope Scholarship Florida Tax Credit']);
    }
  }, []);

  const getRegistrationStatusContent = () => {
    const documentTag = ['documentType', selectedDocument];
    const customerId = getUserState().user?.sub;

    const customerIdTag = ['customerId', customerId];
    const vehicleIdTag = ['vehicleId', vehicleId];

    const registrationTrackerIdTag = ['registrationTrackerId', customerId];

    const contentText = getContentText(registrationStatus, hasMissingDocuments, isDealerInState);

    const dropdownList = possibleDocuments.map((value, index) => ({
      id: index,
      selected: value === selectedDocument,
      value,
    }));

    let displayText: any = contentText;
    if (displayText !== undefined) {
      if (typeof displayText === 'string') {
        let count = 0;
        displayText = (contentText as string)?.split('\n').map((i) => {
          count += 1;
          return <p key={count}>{i}</p>;
        });
      }
    }

    const captureImpression = () => {
      const eventPayload = {
        status,
        daysLeft,
        missingDocuments,
        textTreatment: contentText,
      };
      trackEvent(RegistrationAnalytics.VIEWED, eventPayload);
      datadogRum.addAction(RegistrationAnalytics.VIEWED, eventPayload);
    };
    const { ref } = useIntersectionObserver({
      freezeOnceVisible: true,
      threshold: 0.5,
      onChange: (isIntersecting) => {
        if (isIntersecting && captureImpression) captureImpression();
      },
    });

    return (
      <div ref={ref}>
        {extendedTOPDocument && (extendedTOPDocument as any)?.length > 0 && (
          <WarningBox>
            Carvana is actively working on completing the final step in registering your vehicle and asks for your
            continued patience. We understand you still need to be able to drive your car and are providing you a{' '}
            <TrackerAnchor href={extendedTOPDocument} target="_blank">
              temporary operating plate here
            </TrackerAnchor>
            . Please print it out and put it on your vehicle. We understand the added inconvenience this may bring to
            you, and offer our sincerest apologies for the delay.
          </WarningBox>
        )}
        {(dmvWarningText as any)?.[state] !== undefined && <WarningBox>{(dmvWarningText as any)?.[state]}</WarningBox>}
        <MultilineText>{displayText}</MultilineText>
        {shouldShowMailingInformation && getMailingInfo({ showInFooter: false })}
        {showDocumentUpload && (
          <>
            {missingPaperDocuments && missingPaperDocuments[0] !== '' && (
              <Dropzone>
                <section>
                  <h3>PAPER DOCUMENTS NEEDED:</h3>
                  <PaperWarning>
                    {missingDocuments.length > 1 ? `The following forms ` : `The following form `}
                    must be sent as a hard copy. Please check your email for information on the shipping label provided
                    by Carvana to send your completed form.
                  </PaperWarning>
                  {missingPaperDocuments.map((document) => (
                    <MissingDocument documentName={document} key={document} />
                  ))}
                </section>
              </Dropzone>
            )}
            {missingElectronicDocuments && missingElectronicDocuments[0] !== '' && (
              <>
                <DocUploadArea>
                  <DocumentUpload
                    tags={[customerIdTag, registrationTrackerIdTag, vehicleIdTag, documentTag]}
                    allowSubmit={selectedDocument !== possibleDocuments[0]}
                  >
                    <h3>ELECTRONIC DOCUMENTS NEEDED:</h3>
                    {missingElectronicDocuments.map((document) => (
                      <MissingDocument documentName={document} key={document} />
                    ))}
                    <Dropdown title={selectedDocument} list={dropdownList} resetThenSet={updateDocument} />
                  </DocumentUpload>
                </DocUploadArea>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const getRegistrationStatusFooter = () => {
    if (shouldShowMailingInformation) {
      return getMailingInfo({ showInFooter: true });
    }
    return null;
  };

  const getMailingInfo = ({ showInFooter = false }) => {
    const { mailedOn, trackingId, trackingUrl } = trackingInformation || {};

    return (
      <MailingInfo inFooter={showInFooter}>
        {mailedOn && (
          <MailedOn>
            <Label>Mailed on:</Label>
            <Value>{mailedOn}</Value>
          </MailedOn>
        )}
        {trackingId && (
          <Tracking>
            <Label>Tracking:</Label>
            <Value>
              <LinkWithNativeEvents
                href={trackingUrl}
                target="_blank"
                nativeEvent={{
                  event: 'link',
                  payload: {
                    url: trackingUrl,
                  },
                }}
              >
                {trackingId}
              </LinkWithNativeEvents>
              {/* <a href={trackingUrl}>{trackingId}</a> */}
            </Value>
          </Tracking>
        )}
      </MailingInfo>
    );
  };

  const updateDocument = (index: number) => {
    setSelectedDocument(possibleDocuments[index]);
  };

  return (
    <PostSaleSection
      title="Vehicle Registration"
      subTitle={tempPlateExpirationDate ? <SubTitle expDate={new Date(tempPlateExpirationDate)} /> : <></>}
      isLoading={isRegLoading}
      hasError={hasRegError}
      status={status}
      statusCode={statusCode}
      showFooter={shouldShowMailingInformation}
      footerContent={getRegistrationStatusFooter()}
      sectionItems={[
        {
          sectionType: SECTION_TYPE.REGISTRATION_STATUS,
          sectionData: registrationStatus,
          sectionContent: getRegistrationStatusContent(),
        },
      ]}
      isSelfRegistered={isSelfRegistered}
      showProgress={status !== PROCESSED_STATUS.TOTAL_LOSS}
      hasMissingDocuments={hasMissingDocuments}
      trackEventable
      endDate={completedOn}
    />
  );
};

export default RegistrationCard;
